import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import ConfirmationDialog from '../../../components/confirmationdlg';

import SystemService from '../../../data/system-service';
import Auth from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    hideFileInput: {
        display: 'none',
    },
}));

const WebConfig = () => {
    const classes = useStyles();
    const [web, setWeb] = useState({
        hsts: false,
        key: { name: '' },
        cert: { name: '' },
    });
    const [key, setKey] = useState({ name: '', content: '' });
    const [cert, setCert] = useState({ name: '', content: '' });
    const [hstsDialog, setHSTSDialog] = useState(false);
    const webPerms = Auth.permissions.get('WebServer');

    useEffect(() => {
        SystemService.getWebSettings().then(res => {
            setWeb(res.data);
        }, err => {
            console.error('Error ::: ', err);
        });
    }, []);

    const handleCancel = () => {
        setKey({ name: '', content: '' });
        setCert({ name: '', content: '' });
    };

    const handleToggleHSTSDialog = () => {
        setHSTSDialog(!hstsDialog);
    };

    const handleUpdateHSTS = () => {
        if (web.hsts) {
            SystemService.disableHSTS().then(res => {
                setWeb(res.data);
                toast.success(res.message);
            }, err => {
                console.error(err);
                toast.error(`${err.message}: ${err.error}`);
            });
        } else {
            SystemService.enableHSTS().then(res => {
                setWeb(res.data);
                toast.success(res.message);
            }, err => {
                console.error(err);
                toast.error(`${err.message}: ${err.error}`);
            });
        }
        setHSTSDialog(!hstsDialog);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleKey = (e) => {
        const f = e.target.files[0];
        fileHandler(f).then(content => {
            setKey({
                name: f.name,
                content: content,
            });
        }).catch(err => console.error(err));
    };

    const handleCert = (e) => {
        const f = e.target.files[0];
        fileHandler(f).then(content => {
            setCert({
                name: f.name,
                content: content,
            });
        }).catch(err => console.error(err));
    };

    const handleUploadCertFiles = () => {
        SystemService.uploadWebCerts({ key, cert }).then(res => {
            setCert({ name: '', content: '' });
            setKey({ name: '', content: '' });
            setWeb(res.data);
            toast.success(res.message);
        }, err => {
            console.error(err);
            toast.error(`${err.message}: ${err.error}`);
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid item lg={6}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Box><Typography variant="h6">SSL</Typography></Box>
                        <Box mt={2}>
                            <FormHelperText>
                                Adding a new key/certificate pair will cause the web server
                                to restart and may interrupt existing web sessions.
                            </FormHelperText>
                        </Box>
                        <Box mt={2}>
                            <FormHelperText>
                                If the certificate update fails the original key/certificate pair
                                can be restored using the OpenText™ NDR blue menu with the &quot;Reset Web Server Key/Cert&quot; option.
                            </FormHelperText>
                        </Box>
                        <Box mt={2}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography color="secondary">Current Key:</Typography>
                                {web.key.name}
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography color="secondary">Current Certificate:</Typography>
                                {web.cert.name}
                            </Box>
                        </Box>
                        <Box mt={2}>
                            <Box display="flex">
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    disabled
                                    label="Key"
                                    value={key.name}
                                    InputProps={{
                                        endAdornment: (
                                            <label htmlFor="key-file" style={{ display: 'flex' }}>
                                                {webPerms.write && <Button
                                                    variant="contained"
                                                    color="primary"
                                                    aria-label="upload key file"
                                                    component="span"
                                                >
                                                    Browse
                                                </Button>}
                                            </label>
                                        ),
                                    }}
                                />
                                <input
                                    id="key-file"
                                    className={classes.hideFileInput}
                                    type="file"
                                    onClick={e => { e.target.value = null }}
                                    onChange={handleKey}
                                />
                            </Box>
                        </Box>
                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                disabled
                                label="Certificate"
                                value={cert.name}
                                InputProps={{
                                    endAdornment: (
                                        <label htmlFor="cert-file">
                                            {webPerms.write && <Button
                                                variant="contained"
                                                color="primary"
                                                aria-label="upload cert file"
                                                component="span"
                                            >Browse</Button>}
                                        </label>
                                    ),
                                }}
                            />
                            <input
                                id="cert-file"
                                className={classes.hideFileInput}
                                type="file"
                                onClick={e => { e.target.value = null }}
                                onChange={handleCert}
                            />
                        </Box>
                        {webPerms.write && <Box mt={3} display="flex" justifyContent="flex-end">
                            <Box ml={2}><Button variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button></Box>
                            <Box ml={2}><Button
                                variant="contained"
                                color="primary"
                                aria-label="upload"
                                onClick={handleUploadCertFiles}
                                disabled={!key.content || !cert.content}
                            >Upload</Button></Box>
                        </Box>}
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={6}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Box><Typography variant="h6">HSTS</Typography></Box>
                        <Box mt={2}>
                            <FormHelperText>
                                Enabling/Disabling of HSTS will cause the web server
                                to restart and may interrupt existing web sessions.
                            </FormHelperText>
                        </Box>
                        <Box mt={2}>
                            <FormHelperText>
                                Cannot be enabled with self-signed key/certificate
                            </FormHelperText>
                        </Box>
                        {webPerms.write && <Box mt={3} display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color={web.hsts ? 'secondary' : 'primary'}
                                aria-label="upload"
                                onClick={handleToggleHSTSDialog}
                            >{web.hsts ? 'Disable' : 'Enable'}</Button>
                        </Box>}
                    </Box>
                </Paper>
            </Grid>
            <ConfirmationDialog
                open={hstsDialog}
                title={`${web.hsts ? 'Disable' : 'Enable'} HSTS`}
                message={(() => {
                    return (
                        <Box>
                            <Typography variant="body2">Are you sure you want to {web.hsts ? 'Disable' : 'Enable'} HSTS?</Typography>
                            <Box mt={2} display={web.hsts ? 'none' : 'block'}>
                                <Typography variant="body2">
                                    Enabling HSTS will potentially break other applications that are being
                                    served from the same TLD. Also be aware that inclusion into the HSTS
                                    PreLoad list may take months to remove and may limit your access to this and other tools.
                                </Typography>
                                <Box mt={2}>
                                    <Typography variant="body2" color="secondary">
                                        Proceed with Caution!
                                    </Typography>
                                </Box>
                                <Box mt={2}>
                                    <Link target="_blank" href="https://hstspreload.org/#removal">
                                        https://hstspreload.org/#removal
                                    </Link>
                                </Box>
                            </Box>
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onOK={handleUpdateHSTS}
                onClose={handleToggleHSTSDialog}
            />
        </Grid>
    );
};

export default WebConfig;
